import React, { useEffect, useState } from 'react';

import './ProducerFooter.scss';

import { Button } from '../../FormComponents/Buttons/Button';
import { useMutate } from 'restful-react';
import useProject from '../../../hooks/useProject';
import LoadingSpinner from '../../LoadingSpinner/LoadingSpinner';
import { useTranslation } from 'react-i18next';
import { getUTCDateNow } from '../../../helpers/dateFormatHelper/dateFormatHelper';
import _ from 'lodash';
import history from '../../../tools/history';
import { DeleteProjectModal } from '../../Modals/DeleteProjectModal/DeleteProjectModal';
import { PopupModal } from '../../Modals/PopupModal/PopupModal';
import { Status, StatusEnum } from '../../../types/enums/StatusEnum';
import { CaptionModeEnum } from '../../../types/enums/CaptionModeEnum';
import { captionLanguages } from '../../../common/constants';
import { closedCaptionLanguages } from '../../../common/constants';

type ProducerFooterProps = {};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const ProducerFooter = (props: ProducerFooterProps) => {
  const { project, setProject } = useProject();

  const { t } = useTranslation();
  const [status, setStatus] = useState<Status>(StatusEnum.Created);
  const [refreshing, setRefreshing] = useState<boolean>(false);
  const [checkingCard, setCheckingCard] = useState<boolean>(false);
  const [isNoDestinationsModelOpen, setIsNoDestinationsModelOpen] = useState<boolean>(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [invalidDestinations, setInvalidDestinations] = useState<string[]>([]);

  type DeleteProjectPayload = {
    id: string;
  };

  useEffect(() => {
    const lookupStatus = () => {
      if (project?.broadcastStartedUtc && project?.broadcastEndedUtc) {
        setStatus(StatusEnum.Stopped);
      } else if (project?.broadcastStartedUtc && !project?.broadcastEndedUtc) {
        setStatus(StatusEnum.Ready);
      } else if (!project?.broadcastStartedUtc) {
        setStatus(StatusEnum.Created);
      }
    };

    lookupStatus();
  }, [project]);

  const onStartSuccess = () => {
    setRefreshing(false);
    const newProject = _.cloneDeep(project);

    newProject.broadcastStartedUtc = getUTCDateNow().toString();

    setProject(newProject);
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const onStopSuccess = (body: any, data: any) => {
    setRefreshing(false);
    const newProject = _.cloneDeep(project);

    newProject.broadcastEndedUtc = getUTCDateNow().toString();
    newProject.paymentStatus = data.result.paymentStatus;

    setProject(newProject);
  };

  const onSuccessDelete = () => {
    history.push('/');
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { mutate: startRequest, loading: starting, error: startingError } = useMutate({
    verb: 'POST',
    path: `/projects/${project?.id}/start`,
    onMutate: onStartSuccess,
  });

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { mutate: stopRequest, loading: stopping, error: stoppingError } = useMutate({
    verb: 'POST',
    path: `/projects/${project?.id}/stop`,
    onMutate: onStopSuccess,
  });

  const { mutate: deleteRequest, loading: deleting } = useMutate<DeleteProjectPayload>({
    verb: 'DELETE',
    path: `/projects`,
    onMutate: onSuccessDelete,
  });

  const handleStart = () => {
    let invalidCCDestinations: string[] = [];

    if (project?.encoder?.captionMode === CaptionModeEnum.Closed) {
      const closedCaptionIsos = new Set(closedCaptionLanguages.map(lang => lang.value));
      const allIsoToText = new Map(captionLanguages.map(lang => [lang.value, lang.text]));
      const destinationIsos = (project?.stream?.encoders ?? []).flatMap(e => (e.destinations ?? []).map(dest => dest.languageIsoCode));

      invalidCCDestinations = destinationIsos
        .filter(iso => !closedCaptionIsos.has(iso))
        .map(iso => allIsoToText.get(iso) ?? iso); // fallback to raw ISO if no match
    }

    if (invalidCCDestinations.length > 0) {
      setInvalidDestinations(invalidCCDestinations);
    }
    else {
      setCheckingCard(true);
    }
  };

  const handleStop = (event?: React.BaseSyntheticEvent) => {
    event?.preventDefault();

    stopRequest({});
  };

  const handleDelete = () => {
    setIsModalOpen(true);
  };

  const handleDeleteCancel = () => {
    setIsModalOpen(false);
  };

  const handleDeleteOk = () => {
    const data: DeleteProjectPayload = {
      id: project?.id ?? '',
    };

    deleteRequest(data);
  };

  const isOlderThan = (dateString: string, days: number): boolean => {
    const date = new Date(dateString);
    const now = new Date();
    const threshold = new Date();
    threshold.setDate(now.getDate() - days);
    return date < threshold;
  };

  if (invalidDestinations.length > 0) {
    return (
      <PopupModal
        isOpen={true}
        handleCloseModal={() => setInvalidDestinations([])}
        title={`project.unsupported-cc-destination.title`}
        content={t(`project.unsupported-cc-destination.content`) + invalidDestinations.join('<br/>') + t(`project.unsupported-cc-destination.closing-content`)}
      />
    );
  }

  if (checkingCard) {
    if (!project.createdByUser?.hasCard
      && ((project.stream.encoders?.length ?? 1) > 2
    || (isOlderThan(project.createdByUser?.createdUtc ?? '', 7)))) {
        return (
          <PopupModal
            isOpen={true}
            buttonText='Continue'
            handleCloseModal={() => {
              history.push('/subscribe');
            }}
            title={`project.card-details.title`}
            content={t(`project.card-details.content`)}
          />
        );
    }
    else {
      startRequest({});
    }

    setCheckingCard(false);
  }

  if (starting
    || stopping) {
    return <LoadingSpinner />;
  }

  if (project?.deletedUtc || project?.broadcastEndedUtc || project.stream.status === StatusEnum.Stopped) {
    return null;
  }

  return (
    <div className="main-section-footer level">
      <div className="level-left">
        {status === StatusEnum.Created ? (
          <Button
            translationKey="button.delete-project"
            variant="secondary"
            color="red"
            additionalClassName="btn-no-margin btn-margin-right"
            onClick={handleDelete}
            type="button"
            disabled={refreshing}
          />
        ) : null}
      </div>
      <div className="level-right stop-start-buttons-wrapper">
        {status === StatusEnum.Created ? (
          <Button
            translationKey="button.start-loop"
            variant="primary"
            color="green"
            additionalClassName="btn-no-margin "
            onClick={handleStart}
            type="button"
            disabled={refreshing}
          />
        ) : null}
        {status === StatusEnum.Starting ||
        status === StatusEnum.Ready ||
        status === StatusEnum.Live ? (
          <Button
            translationKey="button.stop-loop"
            variant="primary"
            color="pink"
            additionalClassName="btn-no-margin"
            onClick={handleStop}
            type="button"
            disabled={refreshing}
          />
        ) : null}
        {status === StatusEnum.Stopped ? (
          <Button
            translationKey="button.loop-stopped"
            variant="secondary"
            color="grey"
            additionalClassName="btn-no-margin is-disabled"
            onClick={() => null}
            type="button"
          />
        ) : null}
      </div>
      {isModalOpen && (
        <DeleteProjectModal
          deleting={deleting}
          isOpen={isModalOpen}
          handleCancelModal={handleDeleteCancel}
          handleOkModal={handleDeleteOk}
        />
      )}
      {isNoDestinationsModelOpen && (
        <PopupModal
          isOpen={true}
          handleCloseModal={() => setIsNoDestinationsModelOpen(false)}
          title="producer.no-destinations-modal.title"
          content={t('producer.no-destinations-modal.content')}
        />
      )}
    </div>
  );
};

export default ProducerFooter;
